/**
 * Integrytas Group - DDM (Demo Data Mirror)
 * ddm_mode - per-session production/demo schema switch.
 *
 * Persists the caller's choice in localStorage and exposes it synchronously
 * (mirrors asf.ts's asfWorkspaceCode pattern) so api_client.ts can read it on
 * every gateway call without a React dependency. useDdmSchema() is the
 * reactive form for components (the header toggle, the demo banner).
 *
 * switchDdmSchema() is what the UI should call. Setting the schema alone would
 * leave the app holding security state resolved against the OTHER schema —
 * page access, role assignments, permissions and themes are all read once and
 * cached in React state, so a bare toggle would show production's permissions
 * over demo's data. It re-mints the session and reloads so every provider
 * re-resolves from scratch.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'ddm.schema';
export type DdmSchema = 'public' | 'demo';

function readStorage(): DdmSchema {
  try { return localStorage.getItem(STORAGE_KEY) === 'demo' ? 'demo' : 'public'; } catch { return 'public'; }
}

let current: DdmSchema = readStorage();
const listeners = new Set<() => void>();

/** Synchronous read - this is what api_client.ts sends as x-ddm-schema. */
export function getDdmSchema(): DdmSchema { return current; }

export function setDdmSchema(schema: DdmSchema): void {
  if (schema === current) return;
  current = schema;
  try { localStorage.setItem(STORAGE_KEY, schema); } catch { /* ignore - falls back to in-memory only */ }
  listeners.forEach((l) => l());
}

/**
 * Switch schema, re-establish the session, and reload the app.
 *
 * Three things happen in order, and each is load-bearing:
 *
 *  1. The choice is persisted first, so the reload comes back in the new mode.
 *  2. The session is refreshed, re-minting the JWT the gateway forwards. RLS in
 *     the mirror is decided by the mirror's own helpers, so the caller's
 *     identity has to be re-established rather than carried across on a token
 *     minted for the other side.
 *  3. The page reloads, which is the only reliable way to drop cached security
 *     state — page access, roles and the active theme are resolved once per
 *     session and would otherwise survive the switch.
 *
 * The reload is announced to the remember-me guard, which would otherwise treat
 * it as a tab close and wipe the session of anyone who did not tick "Remember
 * me" — turning a mode switch into a silent sign-out.
 *
 * A failed refresh is not fatal: the reload still re-reads everything, and the
 * session either re-hydrates from storage or the user lands on sign-in, which
 * is the correct outcome for a session that could no longer be refreshed.
 */
export async function switchDdmSchema(next: DdmSchema): Promise<void> {
  if (next === current) return;
  setDdmSchema(next);

  try {
    const [{ supabase }, { beginTrustedReload }] = await Promise.all([
      import('./supabase_client'),
      import('./remember_me'),
    ]);
    await supabase.auth.refreshSession();
    beginTrustedReload();
  } catch {
    // Refresh failed (offline, or a session that cannot be refreshed). Reload
    // anyway — it re-resolves everything, which is the point of the switch.
    try { const { beginTrustedReload } = await import('./remember_me'); beginTrustedReload(); }
    catch { /* remember-me guard absent; nothing to announce to */ }
  }

  window.location.reload();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Reactive read for components (header toggle, demo banner). */
export function useDdmSchema(): DdmSchema {
  return useSyncExternalStore(subscribe, () => current, () => 'public');
}
